/**
 * Public Procurement Client Request — standalone external submission
 * form. Mounted at /procurement/client-request — no auth, no app
 * shell, no sidebar.
 *
 * Mirrors the simplified internal form but takes requester name +
 * email from the body (vs JWT). POSTs to the un-authed
 * /api/procurement/public/client-request endpoint, which validates
 * the chosen internal site is configured and notifies its contacts.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { usePublicPageScroll } from '../../hooks/usePublicPageScroll';
import Select from '../shared/Select';
import Alert from '../shared/Alert';
import Button from '../shared/Button';
import { FIELD_INPUT_CLASS, FIELD_LABEL_CLASS } from '../shared/FormInput';
import { TEXT } from '../shared/Typography';

interface SiteOption {
  siteName: string;
  displayName?: string;
}
interface ClientOption {
  siteName: string;
  displayName?: string;
}

interface FormState {
  requesterName: string;
  requesterEmail: string;
  client: string;
  site: string;
  projectName: string;
  campaign: string;
  internalSite: string;
  workOrderNo: string;
  grpsCtrNumber: string;
  urgent: 'High' | 'Medium' | 'Low' | '';
  needByDate: string;
  description: string;
  procurementType: string[];
  preferredVendors: string;
}

const initialState: FormState = {
  requesterName: '',
  requesterEmail: '',
  client: '',
  site: '',
  projectName: '',
  campaign: '',
  internalSite: '',
  workOrderNo: '',
  grpsCtrNumber: '',
  urgent: '',
  needByDate: '',
  description: '',
  procurementType: [],
  preferredVendors: '',
};

const PROCUREMENT_TYPES = ['Purchase', 'Hire', 'Service', 'GRPS Labour', 'Other'];

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || ''));
}

export default function ProcurementClientRequestApply(): JSX.Element {
  const [state, setState] = useState<FormState>(initialState);
  const [siteOptions, setSiteOptions] = useState<SiteOption[]>([]);
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submittedRef, setSubmittedRef] = useState<string | null>(null);
  const [error, setError] = useState('');

  usePublicPageScroll();

  // Fetch active internal + client sites for the dropdowns
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sitesRes, clientsRes] = await Promise.all([
          axios.get('/api/procurement/public/sites'),
          axios.get('/api/procurement/public/clients'),
        ]);
        if (cancelled) return;
        setSiteOptions(Array.isArray(sitesRes.data?.items) ? sitesRes.data.items : []);
        setClientOptions(Array.isArray(clientsRes.data?.items) ? clientsRes.data.items : []);
      } catch {
        if (!cancelled) {
          setSiteOptions([]);
          setClientOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (patch: Partial<FormState>) => setState((s) => ({ ...s, ...patch }));

  const toggleProcType = (t: string) => {
    setState((s) => {
      const has = s.procurementType.includes(t);
      return {
        ...s,
        procurementType: has
          ? s.procurementType.filter((x) => x !== t)
          : [...s.procurementType, t],
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const errs: string[] = [];
    if (!state.requesterName.trim()) errs.push('Your name is required');
    if (!isValidEmail(state.requesterEmail)) errs.push('A valid email is required');
    if (!state.client.trim()) errs.push('Client is required');
    if (!state.site.trim()) errs.push('Site is required');
    if (!state.projectName.trim()) errs.push('Project name is required');
    if (!state.internalSite) errs.push('Internal site is required');
    if (!state.workOrderNo.trim()) errs.push('Client order number is required');
    if (!state.urgent) errs.push('Urgency is required');
    if (!state.description.trim()) errs.push('Description is required');
    if (errs.length) {
      setError(errs.join(' • '));
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post('/api/procurement/public/client-request', state);
      setSubmittedRef(data?.proReqNumber || data?._id || 'submitted');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e?.response?.data?.error || e?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedRef) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="text-5xl mb-3">✓</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Request Submitted</h1>
          <p className="text-gray-600 mb-4">
            Your reference is{' '}
            <span className={`font-mono ${TEXT.emphasis}`}>{submittedRef}</span>.
            The GRPS team for the chosen site has been notified and will be in touch.
          </p>
          <p className={TEXT.helper}>You may now close this tab.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <header className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">GRPS Procurement Client Request</h1>
          <p className={`mt-1 ${TEXT.bodyMuted}`}>
            Submit a procurement request for one of GRPS&apos;s internal sites. The site team
            will be notified by email when this form is submitted.
          </p>
        </header>

        {error && (
          <Alert variant="error" message={error} className="mb-4" />
        )}

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow-sm p-6 space-y-6"
          autoComplete="off"
        >
          {/* Requester */}
          <section className="space-y-3">
            <h2 className={`border-b pb-2 ${TEXT.cardTitle}`}>
              Your Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={FIELD_LABEL_CLASS}>Name *</label>
                <input
                  type="text"
                  value={state.requesterName}
                  onChange={(e) => update({ requesterName: e.target.value })}
                  className={FIELD_INPUT_CLASS}
                  required
                />
              </div>
              <div>
                <label className={FIELD_LABEL_CLASS}>Email *</label>
                <input
                  type="email"
                  value={state.requesterEmail}
                  onChange={(e) => update({ requesterEmail: e.target.value })}
                  className={FIELD_INPUT_CLASS}
                  required
                />
              </div>
            </div>
          </section>

          {/* Client / Site / Project / Campaign */}
          <section className="space-y-3">
            <h2 className={`border-b pb-2 ${TEXT.cardTitle}`}>
              Client &amp; Project
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={FIELD_LABEL_CLASS}>Client *</label>
                <input
                  type="text"
                  value={state.client}
                  onChange={(e) => update({ client: e.target.value })}
                  className={FIELD_INPUT_CLASS}
                  list="public-procreq-client-options"
                  placeholder="Pick a known client or type a new one"
                  required
                />
                <datalist id="public-procreq-client-options">
                  {clientOptions.map((c) => (
                    <option key={c.siteName} value={c.displayName || c.siteName} />
                  ))}
                </datalist>
              </div>
              <div>
                <label className={FIELD_LABEL_CLASS}>Site *</label>
                <input
                  type="text"
                  value={state.site}
                  onChange={(e) => update({ site: e.target.value })}
                  className={FIELD_INPUT_CLASS}
                  required
                />
              </div>
              <div>
                <label className={FIELD_LABEL_CLASS}>
                  Project Name *
                </label>
                <input
                  type="text"
                  value={state.projectName}
                  onChange={(e) => update({ projectName: e.target.value })}
                  className={FIELD_INPUT_CLASS}
                  required
                />
              </div>
              <div>
                <label className={FIELD_LABEL_CLASS}>Campaign</label>
                <input
                  type="text"
                  value={state.campaign}
                  onChange={(e) => update({ campaign: e.target.value })}
                  className={FIELD_INPUT_CLASS}
                />
              </div>
            </div>
          </section>

          {/* Internal site + WO + Urgency + Need-by */}
          <section className="space-y-3">
            <h2 className={`border-b pb-2 ${TEXT.cardTitle}`}>
              Request Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={FIELD_LABEL_CLASS}>
                  Internal GRPS Site *
                </label>
                <Select
                  variant="compact"
                  className="w-full"
                  required
                  disabled={siteOptions.length === 0}
                  value={state.internalSite}
                  onChange={nextValue => update({ internalSite: nextValue })}
                  options={[
                    { value: '', label: siteOptions.length === 0 ? '(no internal sites configured)' : '-- Select a GRPS site --' },
                    ...siteOptions.map(s => ({ value: s.siteName, label: s.displayName || s.siteName })),
                  ]}
                />
              </div>
              <div>
                <label className={FIELD_LABEL_CLASS}>
                  Client Order Number *
                </label>
                <input
                  type="text"
                  value={state.workOrderNo}
                  onChange={(e) => update({ workOrderNo: e.target.value })}
                  className={FIELD_INPUT_CLASS}
                  required
                />
              </div>
              <div>
                <label className={FIELD_LABEL_CLASS}>
                  GRPS CTR Number
                </label>
                <input
                  type="text"
                  value={state.grpsCtrNumber}
                  onChange={(e) => update({ grpsCtrNumber: e.target.value })}
                  className={FIELD_INPUT_CLASS}
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className={FIELD_LABEL_CLASS}>Urgency *</label>
                <Select
                  variant="compact"
                  placeholder="Select urgency"
                  className="w-full"
                  required
                  value={state.urgent}
                  onChange={nextValue => update({ urgent: nextValue as any })}
                  options={[
                    { value: 'High', label: 'High' },
                    { value: 'Medium', label: 'Medium' },
                    { value: 'Low', label: 'Low' },
                  ]}
                />
              </div>
              <div>
                <label className={FIELD_LABEL_CLASS}>Need-by Date</label>
                <input
                  type="date"
                  value={state.needByDate}
                  onChange={(e) => update({ needByDate: e.target.value })}
                  className={FIELD_INPUT_CLASS}
                />
              </div>
            </div>

            <div>
              <label className={FIELD_LABEL_CLASS}>Description *</label>
              <textarea
                value={state.description}
                onChange={(e) => update({ description: e.target.value })}
                rows={4}
                className={FIELD_INPUT_CLASS}
                placeholder="Describe what's required, including quantities and any other context the GRPS team needs."
                required
              />
            </div>

            <div>
              <label className={FIELD_LABEL_CLASS}>
                Procurement Type
              </label>
              <div className="flex flex-wrap gap-2">
                {PROCUREMENT_TYPES.map((t) => {
                  const checked = state.procurementType.includes(t);
                  return (
                    <Button variant={(checked) ? 'primary' : 'outline'} size="xs" key={t} onClick={() => toggleProcType(t)}>
                      {t}
                    </Button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className={FIELD_LABEL_CLASS}>
                Preferred Vendor(s)
              </label>
              <input
                type="text"
                value={state.preferredVendors}
                onChange={(e) => update({ preferredVendors: e.target.value })}
                className={FIELD_INPUT_CLASS}
                placeholder="Vendor1, Vendor2"
              />
            </div>
          </section>

          <div className="flex justify-end pt-2 border-t">
            <Button variant="primary" size="lg" type="submit" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit Request'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
